/**
 * Proactive session keepalive — drives rolling refresh on a timer so the
 * BFF session never lapses while the user is active.
 *
 * Why this exists:
 *   - Server-side, an access token has a 1h TTL. Refresh is rolling and
 *     happens server-side whenever APISIX forward-auth invokes
 *     `/session/resolve` (i.e. on every `/v1/*` API call). The Redis
 *     SessionTTL is reset on the same code path.
 *   - But a UI page that simply renders content without firing `/v1/*`
 *     for a long stretch — a long form, a slow dashboard, a tab parked
 *     in the background — never triggers refresh. The session
 *     eventually evicts and the user is bounced to login.
 *   - This module hits a dedicated lightweight `GET /session/touch`
 *     endpoint on a timer, which exercises the same refresh path
 *     without exposing tokens or claims.
 *
 * Multi-tab coordination:
 *   Three open tabs of admin-ui + chat-ui + control-ui in the same
 *   browser share the same session cookie. We use Web Locks API
 *   (navigator.locks) to elect a single leader tab; only the leader
 *   fires the keepalive. When the leader closes, the lock releases and
 *   another tab claims leadership. Browsers without Web Locks (very old
 *   Safari) degrade to per-tab keepalive.
 *
 * Visibility:
 *   While the leader tab is hidden (document.hidden === true), the
 *   timer pauses. On becoming visible, if the last successful touch
 *   is older than `intervalMs / 2`, fires immediately to "catch up";
 *   otherwise lets the next scheduled tick run normally.
 *
 * Backoff on failure:
 *   Network errors and 5xx are treated as transient and retried with
 *   exponential backoff (capped at 30s) without resetting auth state.
 *   A 401 confirms the session is gone — delegates to handleUnauthorized.
 */

import type { AuthClient } from './client'

type UnauthorizedProbe = () => Promise<'authenticated' | 'unauthenticated'>

/** Lock name shared across all UIs of a single browser. Coordinates leader. */
export const KEEPALIVE_LOCK_NAME = 'agion-auth-keepalive'

/** Default interval — 75% of access-token TTL (1h * 0.75 = 45 min). */
export const DEFAULT_KEEPALIVE_INTERVAL_MS = 45 * 60 * 1000

/** Cap on exponential backoff for transient failures. */
const MAX_BACKOFF_MS = 30 * 1000

export interface KeepaliveOptions {
  client: AuthClient
  /** Poll interval in ms. Default 45 min. */
  intervalMs?: number
  /** Override touch URL. Default `${authUrl}/session/touch`. */
  touchUrl?: string
  /**
   * Called when a touch fails. Should probe canonical session state and
   * either return 'authenticated' (treat as transient — keep loop alive)
   * or 'unauthenticated' (session truly gone — caller has redirected).
   * Injected to avoid a circular module import; AuthProvider wires the
   * handleUnauthorized helper from `unauthorized.ts` here.
   */
  onUnauthorized?: UnauthorizedProbe
  /** Inject a clock for deterministic tests. Default `() => Date.now()`. */
  now?: () => number
  /** Test injection point — called whenever a touch resolves (success or failure). */
  onTouchResult?: (result: 'ok' | 'transient_error' | 'unauthorized') => void
}

/**
 * Start the keepalive loop. Returns a cancel function. Idempotent if
 * called multiple times — earlier loops are NOT replaced (caller must
 * cancel first if it wants to swap configs).
 */
export function startKeepalive(opts: KeepaliveOptions): () => void {
  const intervalMs = opts.intervalMs ?? DEFAULT_KEEPALIVE_INTERVAL_MS
  const now = opts.now ?? (() => Date.now())

  let stopped = false
  let timerId: ReturnType<typeof setTimeout> | null = null
  // Initialize "stale" so the first visibility resume (or initial leader
  // acquire) always fires a touch. Updated to now() on each success.
  let lastSuccessAt = 0
  let consecutiveFailures = 0
  // Resolved when the holder of the keepalive promise should release the lock.
  let releaseLock: (() => void) | null = null

  // ─── timer body ───────────────────────────────────────────────────────────
  const fireOnce = async () => {
    if (stopped) return

    if (typeof document !== 'undefined' && document.hidden) {
      // Hidden tab — defer until visible. visibilityListener picks it up.
      return
    }

    try {
      const ok = await opts.client.touchSession(opts.touchUrl)
      if (ok) {
        lastSuccessAt = now()
        consecutiveFailures = 0
        opts.onTouchResult?.('ok')
        return
      }

      // touchSession returns false for both 401 and transient errors
      // (a 503 from a sign-in service that cannot check the session).
      // Probe canonical session state via injected handler — only stops
      // the loop on confirmed-gone; an unavailable probe answers
      // 'authenticated'. If no probe is wired, treat as transient.
      const verdict = opts.onUnauthorized
        ? await opts.onUnauthorized()
        : 'authenticated'
      if (verdict === 'unauthenticated') {
        opts.onTouchResult?.('unauthorized')
        // Caller already redirected; nothing more to do.
        stopped = true
        return
      }
      // Probe says we're still authenticated — treat as transient
      consecutiveFailures += 1
      opts.onTouchResult?.('transient_error')
    } catch (err) {
      consecutiveFailures += 1
      console.warn('[auth] keepalive transient failure', err)
      opts.onTouchResult?.('transient_error')
    }
  }

  const scheduleNext = () => {
    if (stopped) return
    if (timerId !== null) {
      clearTimeout(timerId)
      timerId = null
    }
    let delay = intervalMs
    if (consecutiveFailures > 0) {
      // Exponential backoff bounded by both interval and MAX_BACKOFF_MS.
      const backoff = Math.min(
        MAX_BACKOFF_MS,
        1000 * Math.pow(2, Math.min(consecutiveFailures, 5)),
      )
      delay = Math.min(intervalMs, backoff)
    }
    timerId = setTimeout(async () => {
      await fireOnce()
      scheduleNext()
    }, delay)
  }

  // ─── visibility-aware: catch up on resume ─────────────────────────────────
  const onVisibility = async () => {
    if (stopped) return
    if (typeof document === 'undefined') return
    if (document.hidden) return
    if (now() - lastSuccessAt >= intervalMs / 2) {
      await fireOnce()
      scheduleNext()
    }
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility)
  }

  // ─── leader election via Web Locks API ────────────────────────────────────
  const isWebLocksSupported =
    typeof navigator !== 'undefined' &&
    'locks' in navigator &&
    typeof navigator.locks?.request === 'function'

  const runAsLeader = async (): Promise<void> => {
    // Initial "I just became leader" tick, then schedule
    await fireOnce()
    scheduleNext()
    // Hold leadership until cancelled
    await new Promise<void>((resolve) => {
      releaseLock = resolve
    })
  }

  if (isWebLocksSupported) {
    // mode:'exclusive' (the default) blocks until acquired; followers wait.
    // When this tab unloads, the lock releases automatically and a follower
    // becomes the new leader.
    void navigator.locks
      .request(KEEPALIVE_LOCK_NAME, { mode: 'exclusive' }, async () => {
        if (stopped) return
        await runAsLeader()
      })
      .catch((err) => {
        console.warn('[auth] keepalive lock acquisition failed, running unlocked', err)
        // Fall back to unlocked operation
        if (!stopped) void runAsLeader()
      })
  } else {
    // No Web Locks — every tab runs its own keepalive. Degraded but
    // functional. Total traffic scales with tab count, but UX is correct.
    void runAsLeader()
  }

  // ─── cancel ───────────────────────────────────────────────────────────────
  return () => {
    stopped = true
    if (timerId !== null) {
      clearTimeout(timerId)
      timerId = null
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility)
    }
    if (releaseLock) {
      releaseLock()
      releaseLock = null
    }
  }
}
